import { useEffect, useState } from "react";
import { apiRequest, ApiError } from "../../api/client";
import type { AdminAccount } from "../../api/types";
import { useAuth } from "../../context/AuthContext";
import { generatePassword } from "../../utils/passwordGen";

type Role = AdminAccount["role"];
const ROLE_LABEL: Record<Role, string> = { OPERATOR: "Operator", SUPER_ADMIN: "Super admin" };

interface Credentials {
  fullName: string;
  username: string;
  password: string;
  reset: boolean;
}

const suggestUsername = (fullName: string) =>
  fullName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.|\.$/g, "");

function PasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="field">
      <label>Password (at least 10 characters)</label>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          style={{ flex: 1, fontFamily: "ui-monospace, Consolas, monospace", direction: "ltr" }}
        />
        <button type="button" className="btn-ghost" onClick={() => onChange(generatePassword())}>
          Generate
        </button>
      </div>
    </div>
  );
}

function CredentialsModal({ creds, onClose }: { creds: Credentials; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const loginUrl = `${window.location.origin}/operator/login`;
  const text = `AFA Live Interpretation\nSign in: ${loginUrl}\nUsername: ${creds.username}\nPassword: ${creds.password}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{creds.reset ? "Password changed" : "Account created"}</h2>
        <p style={{ marginTop: 0 }}>
          Send these details to <strong>{creds.fullName}</strong>. <strong>The password is shown only now</strong> — it
          can't be looked up later (you can always reset it).
        </p>
        <div className="credentials">{text}</div>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Tip: ask them to change it after signing in (the "Change my password" button in the side menu).
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={copy}>
            {copied ? "Copied ✓" : "Copy details"}
          </button>
          <button className="btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OperatorsPage() {
  const { admin: me } = useAuth();
  const [accounts, setAccounts] = useState<AdminAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [resetting, setResetting] = useState<AdminAccount | null>(null);
  const [editing, setEditing] = useState<AdminAccount | null>(null);
  const [creds, setCreds] = useState<Credentials | null>(null);

  async function load() {
    try {
      setAccounts(await apiRequest<AdminAccount[]>("/admins"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load accounts");
    }
  }

  useEffect(() => {
    if (me?.role === "SUPER_ADMIN") void load();
  }, [me?.role]);

  if (me?.role !== "SUPER_ADMIN") {
    return (
      <div>
        <h1>Operators</h1>
        <p>Only super administrators can manage accounts.</p>
      </div>
    );
  }

  async function update(account: AdminAccount, patch: Partial<Pick<AdminAccount, "role" | "isActive">>) {
    setError(null);
    try {
      await apiRequest(`/admins/${account.id}`, { method: "PATCH", body: patch });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the account");
    }
  }

  return (
    <div>
      <div className="toolbar">
        <h1 style={{ margin: 0, flex: 1 }}>Operators</h1>
        <button className="btn-primary" onClick={() => setShowNew(true)}>
          + New account
        </button>
      </div>
      <p style={{ color: "var(--text-muted)", maxWidth: 760 }}>
        <strong>Operators</strong> can create conferences and sessions, start, pause and stop them, and use the
        microphone. <strong>Super admins</strong> can also manage these accounts and AFA's central dictionary.
        Accounts are disabled rather than deleted, so the history of who ran what is kept.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}
      {accounts === null && !error && <p>Loading…</p>}
      {accounts && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last sign-in</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => {
              const isMe = a.id === me.id;
              return (
                <tr key={a.id}>
                  <td>
                    {a.fullName}
                    {isMe && <span style={{ color: "var(--text-muted)" }}> (you)</span>}
                  </td>
                  <td style={{ direction: "ltr", textAlign: "left" }}>{a.username}</td>
                  <td>
                    <select
                      value={a.role}
                      disabled={isMe}
                      onChange={(e) => update(a, { role: e.target.value as Role })}
                      aria-label={`Role of ${a.fullName}`}
                    >
                      {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`status-pill ${a.isActive ? "ok" : "bad"}`}>
                      <span className="status-dot" />
                      {a.isActive ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td>{a.lastLoginAt ? new Date(a.lastLoginAt).toLocaleString() : "Never"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn-ghost" onClick={() => setEditing(a)}>
                      Edit
                    </button>{" "}
                    <button className="btn-ghost" onClick={() => setResetting(a)}>
                      Change password
                    </button>{" "}
                    {!isMe &&
                      (a.isActive ? (
                        <button
                          className="btn-ghost"
                          onClick={() => {
                            if (confirm(`Disable ${a.fullName}? They will be signed out and can't sign in until you enable the account again.`))
                              void update(a, { isActive: false });
                          }}
                        >
                          Disable
                        </button>
                      ) : (
                        <button className="btn-ghost" onClick={() => update(a, { isActive: true })}>
                          Enable
                        </button>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showNew && (
        <NewAccountModal
          onClose={() => setShowNew(false)}
          onCreated={async (c) => {
            setShowNew(false);
            setCreds(c);
            await load();
          }}
        />
      )}
      {editing && (
        <EditAccountModal
          account={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          account={resetting}
          onClose={() => setResetting(null)}
          onDone={(c) => {
            setResetting(null);
            setCreds(c);
          }}
        />
      )}
      {creds && <CredentialsModal creds={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

function NewAccountModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: Credentials) => void }) {
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [password, setPassword] = useState(() => generatePassword());
  const [role, setRole] = useState<Role>("OPERATOR");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await apiRequest<AdminAccount>("/admins", {
        method: "POST",
        body: { fullName, username, password, role },
      });
      onCreated({ fullName: created.fullName, username: created.username, password, reset: false });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>New account</h2>
        <div className="stack">
          <div className="field">
            <label>Full name</label>
            <input
              value={fullName}
              autoFocus
              onChange={(e) => {
                setFullName(e.target.value);
                if (!usernameEdited) setUsername(suggestUsername(e.target.value));
              }}
            />
          </div>
          <div className="field">
            <label>Username (letters, numbers, dots, dashes)</label>
            <input
              value={username}
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => {
                setUsername(e.target.value);
                setUsernameEdited(true);
              }}
              style={{ direction: "ltr" }}
            />
          </div>
          <PasswordField value={password} onChange={setPassword} />
          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="OPERATOR">Operator — runs sessions</option>
              <option value="SUPER_ADMIN">Super admin — also manages accounts and the central dictionary</option>
            </select>
          </div>
          {error && <div className="error-banner">{error}</div>}
        </div>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || fullName.trim().length < 2 || username.trim().length < 3 || password.length < 10}
            onClick={create}
          >
            {busy ? "Creating…" : "Create account"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditAccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: AdminAccount;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [fullName, setFullName] = useState(account.fullName);
  const [username, setUsername] = useState(account.username);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = fullName.trim() !== account.fullName || username.trim().toLowerCase() !== account.username;
  const usernameChanged = username.trim().toLowerCase() !== account.username;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/admins/${account.id}`, {
        method: "PATCH",
        body: { fullName: fullName.trim(), username: username.trim() },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the changes");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Edit account</h2>
        <div className="stack">
          <div className="field">
            <label>Full name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
          </div>
          <div className="field">
            <label>Username (letters, numbers, dots, dashes)</label>
            <input
              value={username}
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => setUsername(e.target.value)}
              style={{ direction: "ltr" }}
            />
          </div>
          {usernameChanged && (
            <div style={{ fontSize: 13, color: "var(--warn)" }}>
              {account.fullName} will have to sign in with the new username from now on. Their password stays the same.
            </div>
          )}
          {error && <div className="error-banner">{error}</div>}
        </div>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !changed || fullName.trim().length < 2 || username.trim().length < 3}
            onClick={save}
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({
  account,
  onClose,
  onDone,
}: {
  account: AdminAccount;
  onClose: () => void;
  onDone: (c: Credentials) => void;
}) {
  const [password, setPassword] = useState(() => generatePassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reset() {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/admins/${account.id}/password`, { method: "POST", body: { password } });
      onDone({ fullName: account.fullName, username: account.username, password, reset: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset the password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Change password</h2>
        <p style={{ marginTop: 0 }}>
          Set a new password for <strong>{account.fullName}</strong>. They will be signed out everywhere and must use
          the new one to sign in.
        </p>
        <div className="stack">
          <PasswordField value={password} onChange={setPassword} />
          {error && <div className="error-banner">{error}</div>}
        </div>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || password.length < 10} onClick={reset}>
            {busy ? "Saving…" : "Set new password"}
          </button>
        </div>
      </div>
    </div>
  );
}
